// src/app/pages/faq/faq.component.ts

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SeoService } from '../../services/seo.service';

export interface FaqItem {
  question: string;
  answer: string;
  open: boolean;
}

export interface FaqSection {
  heading: string;
  items: FaqItem[];
}

@Component({
  selector: 'app-faq',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.css'],
})
export class FaqComponent implements OnInit {

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  private readonly faqPageId =
    `${this.baseUrl}/faq#faq`;

  constructor(private seo: SeoService) {}

  ngOnInit(): void {

    this.seo.update(
      'Frequently Asked Questions',
      'Frequently asked questions about Lydia Christensen, her campaign for U.S. Senate in Michigan, volunteering, events, donations, and campaign issues.',
      'faq'
    );

    const questions = this.sections.flatMap(
      section => section.items
    );

    this.seo.setStructuredData({
      '@context': 'https://schema.org',

      '@type': 'FAQPage',

      '@id': this.faqPageId,

      'url': `${this.baseUrl}/faq`,

      'name':
        'Frequently Asked Questions About Lydia Christensen',

      'description':
        'Frequently asked questions about Lydia Christensen, her campaign for U.S. Senate in Michigan, volunteering, events, donations, and campaign issues.',

      'isPartOf': {
        '@id': this.seo.websiteId
      },

      'about': {
        '@id': this.seo.lydiaId
      },

      'publisher': {
        '@id': this.seo.campaignId
      },

      'inLanguage': 'en-US',

      'mainEntity': questions.map(item => ({
        '@type': 'Question',

        'name': item.question,

        'acceptedAnswer': {
          '@type': 'Answer',
          'text': item.answer
        }
      }))
    });
  }

  sections: FaqSection[] = [
    {
      heading: 'About the Campaign',
      items: [
        {
          question: 'Why is Lydia running as a Libertarian?',
          answer:
            'Lydia is running as a Libertarian candidate for the United States Senate in Michigan in 2026. Her campaign identifies individual liberty and limited government as central principles.',
          open: true,
        },
        {
          question: 'Where is Lydia from?',
          answer:
            'Lydia’s family is from Ironwood, Michigan, in the Upper Peninsula. She was born in Ashland, Wisconsin, after being transported there because of the lack of hospital infrastructure in the Upper Peninsula at the time.',
          open: false,
        },
        {
          question: 'What office is Lydia running for?',
          answer:
            'Lydia Christensen is running for the United States Senate in Michigan in the 2026 election.',
          open: false,
        },
        {
          question: 'Does Lydia accept corporate donations?',
          answer:
            'Federal law prohibits corporations from making contributions to federal candidate committees. The campaign accepts contributions subject to federal campaign-finance law and applicable contribution limits.',
          open: false,
        },
      ],
    },

    {
      heading: 'The 2026 Election',
      items: [
        {
          question: 'Does Lydia need petition signatures to get on the ballot?',
          answer:
            'Lydia is the Libertarian candidate for U.S. Senate in Michigan. Ballot-access requirements are governed by Michigan election law.',
          open: false,
        },
        {
          question: 'How can I help with canvassing?',
          answer:
            'You can sign up through the Volunteer page to learn about canvassing and other campaign volunteer opportunities.',
          open: false,
        },
        {
          question: 'Who can sign the pledge?',
          answer:
            'The campaign’s pledge is intended for registered Michigan voters who want to indicate their support. See the campaign’s pledge and privacy information for the current requirements.',
          open: false,
        },
        {
          question: 'When is Election Day?',
          answer:
            'The 2026 general election is scheduled for November 3, 2026.',
          open: false,
        },
      ],
    },

    {
      heading: 'Donating',
      items: [
        {
          question: 'What is the maximum I can donate?',
          answer:
            'For the 2025–2026 federal election cycle, an individual may contribute up to $3,500 per election to a federal candidate committee. The primary and general elections are separate elections for contribution-limit purposes.',
          open: false,
        },
        {
          question: 'Who can donate?',
          answer:
            'Federal campaign-finance law limits who may contribute to federal campaigns. U.S. citizens and lawful permanent residents may generally make contributions, while foreign nationals are prohibited from making contributions in connection with U.S. elections. Other federal restrictions also apply.',
          open: false,
        },
        {
          question: 'Are my donations tax-deductible?',
          answer:
            'Contributions to federal political campaigns are not deductible as charitable contributions for federal income-tax purposes.',
          open: false,
        },
        {
          question: 'Why do you need my employer and occupation?',
          answer:
            'Federal campaign-finance reporting rules require political committees to collect certain contributor information, including occupation and employer information in circumstances specified by federal law and FEC regulations.',
          open: false,
        },
        {
          question: 'Do you accept cryptocurrency?',
          answer:
            'The campaign is working on cryptocurrency donation options. Check the campaign’s donation page for the current methods of contribution and any applicable requirements.',
          open: false,
        },
      ],
    },

    {
      heading: 'The Issues',
      items: [
        {
          question: 'What are Lydia’s top priorities?',
          answer:
            'Lydia’s campaign discusses issues including Great Lakes and clean-water protection, housing, tribal sovereignty, civil liberties, economic opportunity, and other issues affecting Michigan communities. See the Issues page for the campaign’s current positions.',
          open: false,
        },
        {
          question: 'What is the Legislative Tracker?',
          answer:
            'The Bill Tracker is a campaign-built tool that tracks legislation moving through Congress and displays the campaign’s stated position on individual bills.',
          open: false,
        },
        {
          question: 'What is the Delegation Report Card?',
          answer:
            'The Report Card is a campaign tool that presents the campaign’s assessment of Michigan’s congressional delegation based on selected legislative votes and issues.',
          open: false,
        },
      ],
    },

    {
      heading: 'Staying Connected',
      items: [
        {
          question: 'How do I stay updated on the campaign?',
          answer:
            'You can sign up for the campaign newsletter, follow the campaign’s official social-media accounts, and visit the Newsroom and Events pages for updates.',
          open: false,
        },
        {
          question: 'How do I invite Lydia to my event?',
          answer:
            'Visit the Events page and use the “Invite Lydia to Your Event” form to submit the event details to the campaign.',
          open: false,
        },
        {
          question: 'How do I contact the campaign directly?',
          answer:
            'Use the Contact page on this website to get in touch with the campaign. Press inquiries can be directed through the Press page.',
          open: false,
        },
      ],
    },
  ];

  toggle(section: FaqSection, item: FaqItem): void {
    item.open = !item.open;
  }
}